import { Tooltip } from "primereact/tooltip";
import React from "react";

// Format dates in a timezone-safe way (moved outside for broader access)
const formatDateToYYYYMMDD = (dateObj) => {
  if (!dateObj) return null; // Handle cases where date might be null
  const d = new Date(dateObj);
  // Check for invalid date object
  if (isNaN(d.getTime())) {
    console.warn("Invalid date object passed to formatDateToYYYYMMDD:", dateObj);
    return null;
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const DayView = ({ date, tasks, onTaskClick }) => {
  console.log("DayView tasks:", tasks);
  // Format date for display
  const formattedDate = new Date(date).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Create an array of hours for display (just working hours)
  const displayHours = ["10am", "11am", "12pm", "1pm", "2pm", "3pm", "4pm", "5pm", "6pm", "6:30pm"];

  // Working hours range (for calculations)
  const workingHoursStart = 10; // 10am
  const workingHoursEnd = 18.5; // 6:30pm
  const workingHoursLength = workingHoursEnd - workingHoursStart;

  // Helper to get task status color
  const getStatusColor = (status) => {
    switch (status) {
      case "To Do":
        return "#6c757d"; // secondary
      case "In Progress":
        return "#007bff"; // primary
      case "Review":
        return "#ffc107"; // warning
      case "Done":
        return "#28a745"; // success
      case "Pause":
        return "#dc3545"; // red
      default:
        return "#6c757d"; // secondary
    }
  };

  // Helper to get status class for the task
  const getStatusClass = (status) => {
    switch (status) {
      case "To Do":
        return "status-todo";
      case "In Progress":
        return "status-progress";
      case "Review":
        return "status-review";
      case "Done":
        return "status-done";
      default:
        return "status-todo";
    }
  };

  const createTimeObject = (startTime, endTime) => {
    const parseTime = (timeStr) => {
      const [time, period] = timeStr.split(" ");
      let [hours, minutes] = time.split(":").map(Number);

      if (period === "PM" && hours !== 12) {
        hours += 12;
      } else if (period === "AM" && hours === 12) {
        hours = 0;
      }

      return { hours, minutes: minutes || 0 };
    };

    const start = parseTime(startTime);
    const end = parseTime(endTime);

    // Calculate duration in hours
    const startInMinutes = start.hours * 60 + start.minutes;
    const endInMinutes = end.hours * 60 + end.minutes;
    const durationInMinutes = endInMinutes - startInMinutes;
    const durationInHours = durationInMinutes / 60;

    return { start, end, durationInHours };
  };

  // Add debug logging in processTaskData to help diagnose issues
  const processTaskData = (backendTasks) => {
    // console.log("Raw backend tasks:", backendTasks);

    if (!backendTasks || backendTasks.length === 0) {
      // console.log("No tasks to process");
      return [];
    }

    const selectedDateStr = formatDateToYYYYMMDD(date);
    const todayStr = formatDateToYYYYMMDD(new Date());

    // Function to calculate days between two dates
    const daysBetween = (startDateStr, endDateStr) => {
      const start = new Date(startDateStr);
      const end = new Date(endDateStr);
      // Reset time parts to ensure accurate day calculation
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      // Calculate difference in days
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };

    // Filter tasks based on new requirements:
    // 1. Only show tasks that have actually started (actual_start_date exists)
    // 2. Only show tasks with status higher than "To Do"
    const filteredTasks = backendTasks.filter((task) => {
      // Check if the task has actually started
      const hasActuallyStarted = !!task.actual_start_date;

      // Check if the status is higher than "To Do"
      const hasValidStatus = task.status && task.status !== "To Do";

      return hasActuallyStarted && hasValidStatus;
    });

    // console.log("Filtered tasks (actual_start_date and status > To Do):", filteredTasks);

    // If no tasks remain after filtering, return empty array
    if (filteredTasks.length === 0) {
      // console.log("No tasks after filtering");
      return [];
    }

    const processedTasks = filteredTasks.map((task) => {
      // Determine most appropriate dates to use
      // For start date: Use actual_start_date if available, otherwise use start_date
      // For tasks spanning multiple days, if the selected day is not the start date,
      // we'll set the start time to the beginning of the work day

      // Parse dates in a timezone-safe way
      const taskStartDateStr = task.actual_start_date ? formatDateToYYYYMMDD(new Date(task.actual_start_date)) : task.start_date;
      const taskEndDateStr = task.actual_end_date ? formatDateToYYYYMMDD(new Date(task.actual_end_date)) : task.end_date;

      // Calculate days running for continuing tasks
      const daysRunning = taskStartDateStr < selectedDateStr ? daysBetween(taskStartDateStr, selectedDateStr) : 0;

      let startDate, endDate;

      // Handle task display for the selected day
      if (taskStartDateStr === selectedDateStr) {
        // Task starts on the selected day - use actual time if available
        startDate = task.actual_start_date ? new Date(task.actual_start_date) : new Date(`${task.start_date}T10:00:00`);
      } else if (taskStartDateStr < selectedDateStr) {
        // Task started before the selected day - show from beginning of work day
        startDate = new Date(`${selectedDateStr}T10:00:00`);
      } else {
        // Fallback (should not happen with our query, but just in case)
        startDate = new Date(`${selectedDateStr}T10:00:00`);
      }

      if (taskEndDateStr === selectedDateStr) {
        // Task ends on the selected day - use actual time if available
        endDate = task.actual_end_date ? new Date(task.actual_end_date) : new Date(`${task.end_date}T18:30:00`);
      } else if (taskEndDateStr > selectedDateStr || !task.actual_end_date) {
        // Task continues beyond selected day or is not completed - show until end of work day
        endDate = new Date(`${selectedDateStr}T18:30:00`);
      } else {
        // Fallback (should not happen with our query, but just in case)
        endDate = new Date(`${selectedDateStr}T18:30:00`);
      }

      // console.log(`Processing task ${task.id}: ${task.title}`);
      // console.log(`  Start date: ${startDate}`);
      // console.log(`  End date: ${endDate}`);

      // Calculate duration in hours - limit to same day view
      const startTime = startDate.getHours() + startDate.getMinutes() / 60;
      const endTime = endDate.getHours() + endDate.getMinutes() / 60;

      // Ensure the duration makes sense for this day's view
      // If the task spans multiple days, we'll only show the portion for this day
      const durationInHours = Math.min(endTime - startTime, 8); // Cap at 8 hours max for display

      // Return formatted task with times appropriate for the current day's view
      return {
        id: task.id,
        title: task.title,
        description: task.description || "No description",
        status: task.status || "To Do",
        developers: task.developers || [],
        project: task.project || { title: "Unassigned" },
        task_type: task.taskType || { task_type: "Task" },
        duration: durationInHours > 0 ? durationInHours : 1, // Ensure minimum duration
        updated_at: startDate.toISOString(), // Use as timestamp for positioning

        // Add task metadata to help with debugging and displaying details
        start_date: task.start_date,
        end_date: task.end_date,
        actual_start_date: task.actual_start_date,
        actual_end_date: task.actual_end_date,
        status_history: task.status_history || [],
        selected_date: selectedDateStr,
        is_continuing: taskStartDateStr < selectedDateStr || taskEndDateStr > selectedDateStr,
        daysRunning: daysRunning, // Add the number of days this task has been running
      };
    });

    // console.log("Processed tasks:", processedTasks);
    return processedTasks;
  };

  // Use provided tasks from backend or empty array if not available
  const calendarTasks = tasks && tasks.length > 0 ? processTaskData(tasks) : [];

  // Format time in 12-hour format with cleaner appearance
  const formatTime = (hours, minutes) => {
    const hour = hours % 12 || 12;
    const minute = minutes.toString().padStart(2, "0");
    const period = hours < 12 ? "a" : "p"; // Just use 'a' or 'p' to save space

    // For :00, don't show minutes
    if (minutes === 0) {
      return `${hour}${period}`;
    }
    return `${hour}:${minute}${period}`;
  };

  // Calculate task position and dimensions - adjust for business hours view
  const calculateTaskPosition = (task) => {
    const startDate = new Date(task.updated_at);
    const startHour = startDate.getHours();
    const startMinute = startDate.getMinutes();

    // Check if the task is unfinished (no actual end date)
    const isUnfinished = !task.actual_end_date;

    // For ANY unfinished tasks, we want them to visually end at workingHoursEnd (6:30pm)
    let endHour, endMinute;

    if (isUnfinished) {
      // For tasks that started on previous days or today and are not finished,
      // force the visual end time to be the end of the workday (6:30pm)
      endHour = Math.floor(workingHoursEnd);
      endMinute = Math.round((workingHoursEnd - endHour) * 60);
    } else {
      // Use existing duration for normal (completed) tasks
      const duration = task.duration || 1;

      // Calculate end time based on duration
      const rawEndTime = startHour + duration + startMinute / 60;
      endHour = Math.floor(rawEndTime);
      endMinute = Math.round((rawEndTime - endHour) * 60);
    }

    // Align start time to nearest 30-min increment
    const roundedStartMinutes = Math.floor(startMinute / 30) * 30;
    const roundedStartHour = startHour + (roundedStartMinutes === 60 ? 1 : 0);
    const actualRoundedStartMinutes = roundedStartMinutes === 60 ? 0 : roundedStartMinutes;

    // Adjust hours to fit in our display window (10am-6:30pm)
    const adjustedStartHour = Math.max(workingHoursStart, Math.min(workingHoursEnd, roundedStartHour + actualRoundedStartMinutes / 60));
    const displayStart = adjustedStartHour - workingHoursStart;

    const roundedEndMinutes = Math.ceil(endMinute / 30) * 30;
    const roundedEndHour = endHour + (roundedEndMinutes === 60 ? 1 : 0);
    const actualRoundedEndMinutes = roundedEndMinutes === 60 ? 0 : roundedEndMinutes;

    // Ensure minimum task duration of 30 minutes for visibility
    const taskEndTime = roundedEndHour + actualRoundedEndMinutes / 60;
    const adjustedEndTime = Math.min(workingHoursEnd, Math.max(adjustedStartHour + 0.5, taskEndTime));
    const displayDuration = adjustedEndTime - adjustedStartHour;

    // Calculate percentage positions for grid
    const startPercentage = (displayStart / workingHoursLength) * 100;
    const widthPercentage = (displayDuration / workingHoursLength) * 100;

    // Format times for display (use original times, not rounded ones)
    const formattedStartTime = formatTime(startHour, startMinute);
    // For ANY unfinished task, display "Running" instead of an end time
    const formattedEndTime = isUnfinished ? "Running" : formatTime(endHour, endMinute);

    return {
      left: `${startPercentage}%`,
      width: `${widthPercentage}%`,
      startTime: formattedStartTime,
      endTime: formattedEndTime,
      // Adjust label: If end time is 'Running', don't show the hyphen or end time.
      timeLabel: `${formattedStartTime}${formattedEndTime === "Running" || widthPercentage < 15 ? "" : ` - ${formattedEndTime}`}`,
      verticalPosition: 0, // Will be set by organizeTasksPositions
      isOutsideWorkingHours: startHour < workingHoursStart || startHour >= workingHoursEnd,
    };
  };

  // Calculate current time indicator position based on business hours
  const getCurrentTimePosition = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentTime = hours + minutes / 60;

    // If outside working hours, don't show
    if (currentTime < workingHoursStart || currentTime > workingHoursEnd) {
      return {
        left: "-100%", // Hide it off-screen
        time: formatTime(hours, minutes),
      };
    }

    // Calculate position within working hours
    const timePosition = currentTime - workingHoursStart;
    const percentage = (timePosition / workingHoursLength) * 100;

    return {
      left: `${percentage}%`,
      time: formatTime(hours, minutes),
    };
  };

  // Check if two tasks overlap in time
  const tasksOverlap = (task1, task2) => {
    const start1 = new Date(task1.updated_at);
    const end1 = new Date(start1);
    end1.setHours(end1.getHours() + (task1.duration || 1));

    const start2 = new Date(task2.updated_at);
    const end2 = new Date(start2);
    end2.setHours(end2.getHours() + (task2.duration || 1));

    return start1 < end2 && start2 < end1;
  };

  // Completely revise task positioning and organization
  const organizeTasksPositions = (tasks) => {
    if (!tasks || tasks.length === 0) return [];

    // Sort tasks by start time first
    const sortedTasks = [...tasks].sort((a, b) => {
      const startA = new Date(a.updated_at);
      const startB = new Date(b.updated_at);
      return startA - startB;
    });

    // Group tasks that would overlap in time
    const taskGroups = [];

    // For each task, find a group it fits in or create a new group
    sortedTasks.forEach((task) => {
      const taskStart = new Date(task.updated_at);
      const taskEnd = new Date(taskStart);
      taskEnd.setHours(taskEnd.getHours() + (task.duration || 1));

      // Find a group where this task doesn't overlap with any task
      let foundGroup = false;
      for (let groupIndex = 0; groupIndex < taskGroups.length; groupIndex++) {
        const group = taskGroups[groupIndex];

        // Check if task overlaps with any task in this group
        const overlapsWithGroup = group.some((groupTask) => {
          const groupTaskStart = new Date(groupTask.updated_at);
          const groupTaskEnd = new Date(groupTaskStart);
          groupTaskEnd.setHours(groupTaskEnd.getHours() + (groupTask.duration || 1));

          return taskStart < groupTaskEnd && groupTaskStart < taskEnd;
        });

        if (!overlapsWithGroup) {
          // Task doesn't overlap with any task in this group, add it
          group.push(task);
          foundGroup = true;
          break;
        }
      }

      // If no suitable group found, create a new one
      if (!foundGroup) {
        taskGroups.push([task]);
      }
    });

    // Assign row positions based on the group
    const tasksWithPositions = [];

    taskGroups.forEach((group, groupIndex) => {
      group.forEach((task) => {
        tasksWithPositions.push({
          ...task,
          rowIndex: groupIndex, // Use row index for vertical positioning
        });
      });
    });

    return tasksWithPositions;
  };

  // Update developer tasks to use real data
  const getTasksByDeveloperOrganized = () => {
    const developerMap = new Map();

    // Add "Unassigned" category
    developerMap.set("unassigned", []);

    calendarTasks.forEach((task) => {
      if (!task.developers || task.developers.length === 0) {
        developerMap.get("unassigned").push(task);
      } else {
        task.developers.forEach((dev) => {
          const devId = dev.id;
          const devName = dev.name || `${dev.first_name || ""} ${dev.last_name || ""}`.trim() || `ID: ${dev.id}`;

          if (!developerMap.has(devId)) {
            developerMap.set(devId, {
              name: devName,
              tasks: [],
            });
          }

          // Add task to this developer
          developerMap.get(devId).tasks.push(task);
        });
      }
    });

    // Organize tasks for each developer to avoid overlaps
    developerMap.forEach((value, key) => {
      if (key === "unassigned") {
        // Use the new organizeTasksPositions function to get clean rows
        developerMap.set(key, organizeTasksPositions(value));
      } else {
        developerMap.set(key, {
          ...value,
          tasks: organizeTasksPositions(value.tasks),
        });
      }
    });

    return developerMap;
  };

  const developerTasks = getTasksByDeveloperOrganized();

  // Remove any previous position attributes - data-vertical-position is no longer needed
  const calculateGroupHeight = (tasks) => {
    if (!tasks || tasks.length === 0) return 80; // Minimum height

    // Calculate the total height needed for tasks
    // Each task row is 80px high plus 15px margin
    const taskHeight = 95; // 80px + 15px margin
    const taskCount = Array.isArray(tasks) ? tasks.length : tasks.tasks.length;

    // Calculate total height needed with padding
    return Math.max(80, taskCount * taskHeight + 20); // Add 20px padding
  };

  return (
    <div className="day-view">
      <h2 className="day-header">{formattedDate}</h2>

      <div className="calendar-container">
        {/* Hour headers */}
        <div className="time-header">
          <div className="developer-column">Developer</div>
          <div className="hours-wrapper">
            {displayHours.map((hour, index) => {
              // Check if this hour is the current hour
              const currentHour = new Date().getHours();
              const currentMinutes = new Date().getMinutes();
              const hourValue = hour.includes("am")
                ? parseInt(hour.replace("am", ""))
                : hour.includes(":")
                ? parseInt(hour.split(":")[0]) + (hour.includes("12:") ? 0 : 12)
                : parseInt(hour.replace("pm", "")) + (hour.includes("12pm") ? 0 : 12);
              // For 6:30pm, check if it's between 6:00-6:59 PM and past 30 minutes
              const isCurrent =
                hourValue === 18 && hour.includes(":30")
                  ? currentHour === 18 && currentMinutes >= 30
                  : hourValue === currentHour && (hour.includes(":") ? currentMinutes >= 30 : currentMinutes < 30);

              return (
                <div key={index} className={`hour-column ${isCurrent ? "current" : ""}`}>
                  {hour}
                </div>
              );
            })}
          </div>
        </div>

        {/* Developer rows */}
        <div className="developer-rows">
          {/* Unassigned row */}
          {developerTasks.get("unassigned") && developerTasks.get("unassigned").length > 0 && (
            <div className="calendar-row" style={{ minHeight: `${calculateGroupHeight(developerTasks.get("unassigned"))}px` }}>
              <div className="developer-column">Unassigned</div>
              <div className="time-slots" style={{ minHeight: `${calculateGroupHeight(developerTasks.get("unassigned"))}px` }}>
                <div className="tasks-container">
                  {developerTasks.get("unassigned").map((task, index) => {
                    const position = calculateTaskPosition(task);
                    const isLunchBreak = task.title.toLowerCase().includes("lunch");

                    // Skip tasks outside working hours
                    if (position.isOutsideWorkingHours) return null;

                    const taskId = isLunchBreak ? `lunch-break-${task.id}` : `task-${task.id}`;

                    return (
                      <div key={task.id} className="task-row">
                        <Tooltip
                          target={`#${taskId}`}
                          content={`${task.title}: ${task.description || "No description"}`}
                          position="top"
                          className="task-tooltip"
                          showDelay={200}
                        />
                        <div
                          className={`task-bar ${getStatusClass(task.status)} ${isLunchBreak ? "lunch-break" : ""} ${
                            task.is_continuing ? "continuing-task" : ""
                          }`}
                          style={{
                            left: position.left,
                            width: position.width,
                            backgroundColor: getStatusColor(task.status),
                            top: "0px",
                          }}
                          onClick={() => onTaskClick(task)}
                          id={taskId}
                        >
                          <div className="task-time">{`${position.startTime} - ${position.endTime}`}</div>
                          <div className="task-title">
                            {task.is_continuing && (
                              <span
                                className="continuing-badge"
                                title={`This task has been running for ${task.daysRunning} day${task.daysRunning !== 1 ? "s" : ""}`}
                              >
                                <i className="pi pi-calendar mr-1"></i>
                                {task.daysRunning > 0 && <span className="days-count">{task.daysRunning}d</span>}
                              </span>
                            )}
                            {task.title}
                          </div>
                          {task.project && <div className="task-project">{task.project.title}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Hour grid lines */}
                <div className="grid-lines">
                  {displayHours.map((hour, index) => {
                    const currentHour = new Date().getHours();
                    const currentMinutes = new Date().getMinutes();
                    const hourValue = hour.includes("am")
                      ? parseInt(hour.replace("am", ""))
                      : hour.includes(":")
                      ? parseInt(hour.split(":")[0]) + (hour.includes("12:") ? 0 : 12)
                      : parseInt(hour.replace("pm", "")) + (hour.includes("12pm") ? 0 : 12);
                    // For 6:30pm, check if it's between 6:00-6:59 PM and past 30 minutes
                    const isCurrent =
                      hourValue === 18 && hour.includes(":30")
                        ? currentHour === 18 && currentMinutes >= 30
                        : hourValue === currentHour && (hour.includes(":") ? currentMinutes >= 30 : currentMinutes < 30);

                    return <div key={index} className={`grid-line ${isCurrent ? "current" : ""}`}></div>;
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Developer specific rows */}
          {Array.from(developerTasks.entries()).map(([devId, value]) => {
            if (devId === "unassigned") return null;

            return (
              <div key={devId} className="calendar-row" style={{ minHeight: `${calculateGroupHeight(value)}px` }}>
                <div className="developer-column">{value.name}</div>
                <div className="time-slots" style={{ minHeight: `${calculateGroupHeight(value)}px` }}>
                  <div className="tasks-container">
                    {value.tasks.map((task, index) => {
                      const position = calculateTaskPosition(task);
                      const isLunchBreak = task.title.toLowerCase().includes("lunch");

                      // Skip tasks outside working hours
                      if (position.isOutsideWorkingHours) return null;

                      const taskId = isLunchBreak ? `lunch-break-${task.id}-${devId}` : `task-${task.id}-${devId}`;

                      return (
                        <div key={task.id} className="task-row">
                          <Tooltip
                            target={`#${taskId}`}
                            content={`${task.title}: ${task.description || "No description"}`}
                            position="top"
                            className="task-tooltip"
                            showDelay={200}
                          />
                          <div
                            className={`task-bar ${getStatusClass(task.status)} ${isLunchBreak ? "lunch-break" : ""} ${
                              task.is_continuing ? "continuing-task" : ""
                            }`}
                            style={{
                              left: position.left,
                              width: position.width,
                              backgroundColor: getStatusColor(task.status),
                              top: "0px",
                            }}
                            onClick={() => onTaskClick(task)}
                            id={taskId}
                          >
                            <div className="task-time">{`${position.startTime} - ${position.endTime}`}</div>
                            <div className="task-title">
                              {task.is_continuing && (
                                <span
                                  className="continuing-badge"
                                  title={`This task has been running for ${task.daysRunning} day${task.daysRunning !== 1 ? "s" : ""}`}
                                >
                                  <i className="pi pi-calendar mr-1"></i>
                                  {task.daysRunning > 0 && <span className="days-count">{task.daysRunning}d</span>}
                                </span>
                              )}
                              {task.title}
                            </div>
                            {task.project && <div className="task-project">{task.project.title}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Hour grid lines */}
                  <div className="grid-lines">
                    {displayHours.map((hour, index) => {
                      const currentHour = new Date().getHours();
                      const currentMinutes = new Date().getMinutes();
                      const hourValue = hour.includes("am")
                        ? parseInt(hour.replace("am", ""))
                        : hour.includes(":")
                        ? parseInt(hour.split(":")[0]) + (hour.includes("12:") ? 0 : 12)
                        : parseInt(hour.replace("pm", "")) + (hour.includes("12pm") ? 0 : 12);
                      // For 6:30pm, check if it's between 6:00-6:59 PM and past 30 minutes
                      const isCurrent =
                        hourValue === 18 && hour.includes(":30")
                          ? currentHour === 18 && currentMinutes >= 30
                          : hourValue === currentHour && (hour.includes(":") ? currentMinutes >= 30 : currentMinutes < 30);

                      return <div key={index} className={`grid-line ${isCurrent ? "current" : ""}`}></div>;
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend for task statuses */}
      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-color legend-todo"></div>
          <span>To Do</span>
        </div>
        <div className="legend-item">
          <div className="legend-color legend-progress"></div>
          <span>In Progress</span>
        </div>
        <div className="legend-item">
          <div className="legend-color legend-review"></div>
          <span>Review</span>
        </div>
        <div className="legend-item">
          <div className="legend-color legend-done"></div>
          <span>Done</span>
        </div>
      </div>

      <style>{`
        .day-view {
          padding: 1rem;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
        }

        .day-header {
          text-align: center;
          margin-bottom: 1.5rem;
          font-weight: 500;
          color: #333;
          font-size: 1.75rem;
        }

        .calendar-container {
          border: 1px solid #ddd;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 3px 8px rgba(0,0,0,0.1);
          background-color: #fff;
        }

        .time-header {
          display: flex;
          border-bottom: 1px solid #ddd;
          background-color: #f8f9fa;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .hours-wrapper {
          display: flex;
          flex: 1;
        }

        .hour-column {
          flex: 1;
          text-align: center;
          padding: 12px 0;
          font-size: 0.9rem;
          font-weight: 500;
          color: #444;
          border-left: 1px solid #e9ecef;
          transition: background-color 0.2s;
          background-color: rgba(235, 245, 251, 0.4);
        }

        .hour-column.current {
          background-color: rgba(244, 67, 54, 0.08);
        }

        .developer-column {
          width: 180px;
          min-width: 180px;
          padding: 10px 12px;
          font-weight: 500;
          background-color: #f8f9fa;
          border-right: 1px solid #ddd;
          display: flex;
          align-items: center;
          color: #444;
        }

        .developer-rows {
          display: flex;
          flex-direction: column;
        }

        .calendar-row {
          display: flex;
          border-bottom: 1px solid #ddd;
          min-height: 80px; /* Base minimum height */
        }

        .calendar-row:last-child {
          border-bottom: none;
        }

        .calendar-row:hover {
          background-color: rgba(0,0,0,0.02);
        }

        .time-slots {
          flex: 1;
          position: relative;
          min-height: 80px;
          background-color: rgba(235, 245, 251, 0.4);
          padding: 5px 0;
        }

        /* Row-based task container */
        .tasks-container {
          display: flex;
          flex-direction: column;
          position: relative;
          width: 100%;
          height: 100%;
        }

        /* Each task gets its own row */
        .task-row {
          position: relative;
          width: 100%;
          height: 80px;
          margin-bottom: 5px;
        }

        /* Grid lines for time slots */
        .grid-lines {
          display: flex;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
          pointer-events: none;
        }

        .grid-line {
          flex: 1;
          height: 100%;
          border-left: 1px dashed #d0d0d0;
        }

        .grid-line.current {
          background-color: rgba(244, 67, 54, 0.08);
        }

        /* Task styling */
        .task-bar {
          position: absolute;
          height: 80px;
          border-radius: 8px;
          padding: 12px 15px;
          z-index: 5;
          color: white;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 13px;
          border: 1px solid rgba(0,0,0,0.15);
        }

        .task-bar:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 10px rgba(0,0,0,0.25);
          z-index: 20;
        }

        /* Task content */
        .task-time {
          font-size: 0.8rem;
          opacity: 0.95;
          font-weight: 600;
          margin-bottom: 5px;
          letter-spacing: 0.2px;
          background-color: rgba(0,0,0,0.15);
          display: inline-block;
          padding: 2px 6px;
          border-radius: 4px;
        }

        .task-title {
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-top: 0;
          font-size: 0.9rem;
          line-height: 1.2;
          max-height: 2.4em;
        }
        
        .task-title:hover {
          white-space: normal;
        }
        
        .task-project {
          font-size: 0.75rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          opacity: 0.9;
          margin-top: 2px;
          display: block;
        }

        /* Status styling */
        .status-todo {
          border-left: 5px solid #545b62;
        }

        .status-progress {
          border-left: 5px solid #0056b3;
        }

        .status-review {
          border-left: 5px solid #d39e00;
        }

        .status-done {
          border-left: 5px solid #1e7e34;
        }

        /* Special task types */
        .task-bar.lunch-break {
          background-color: #8d6e63 !important;
          border-left: 4px solid #6d4c41;
          opacity: 0.85;
          z-index: 4;
        }

        .task-bar.continuing-task {
          border-left-width: 5px;
          border-left-style: dashed;
          border-left-color: rgba(255, 255, 255, 0.8);
          padding-left: 12px;
        }

        /* Continuing badge styles */
        .continuing-badge {
          display: inline-flex;
          align-items: center;
          margin-right: 5px;
          background-color: rgba(0, 0, 0, 0.15);
          border-radius: 4px;
          padding: 1px 5px;
          font-size: 10px;
        }
        
        .days-count {
          font-weight: bold;
          margin-left: 2px;
        }

        /* Legend styling */
        .calendar-legend {
          display: flex;
          justify-content: center;
          margin-top: 15px;
          gap: 20px;
          flex-wrap: wrap;
        }
        
        .legend-item {
          display: flex;
          align-items: center;
          font-size: 0.8rem;
          color: #555;
        }
        
        .legend-color {
          width: 15px;
          height: 15px;
          margin-right: 5px;
          border-radius: 3px;
          border-left: 3px solid;
        }
        
        .legend-todo {
          background-color: #6c757d;
          border-left-color: #545b62;
        }
        
        .legend-progress {
          background-color: #007bff;
          border-left-color: #0056b3;
        }
        
        .legend-review {
          background-color: #ffc107;
          border-left-color: #d39e00;
        }
        
        .legend-done {
          background-color: #28a745;
          border-left-color: #1e7e34;
        }
      `}</style>
    </div>
  );
};

export default DayView;
